import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BadgeCheck, Clock3, MapPinned, RefreshCw, Send } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REQUEST_TYPES = [
  { value: 'leave', label: 'Leave' },
  { value: 'time_correction', label: 'Time correction / change' },
  { value: 'permission', label: 'Permission' },
  { value: 'camp_duty', label: 'Camp duty' },
];
const ATTENDANCE_TYPES = ['regular', 'camp', 'relieving_duty'];

const emptyRequest = {
  request_type: 'leave',
  start_date: new Date().toISOString().split('T')[0],
  end_date: new Date().toISOString().split('T')[0],
  time_from: '',
  time_to: '',
  location: '',
  reason: '',
  attendance_record_id: '',
};

const requestLabel = (value) => REQUEST_TYPES.find((item) => item.value === value)?.label || value?.replaceAll('_', ' ');
const formatIstTime = (value) => value ? new Date(value).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : '-';
const formatDuration = (minutes) => {
  if (minutes === null || minutes === undefined) return 'In progress';
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

const getGpsStamp = () => new Promise((resolve, reject) => {
  if (!navigator.geolocation) {
    reject(new Error('Geolocation is not supported on this device'));
    return;
  }
  navigator.geolocation.getCurrentPosition(
    (position) => {
      const gpsLat = Number(position.coords.latitude.toFixed(6));
      const gpsLng = Number(position.coords.longitude.toFixed(6));
      resolve({
        gps_lat: gpsLat,
        gps_lng: gpsLng,
        gps_map_link: `https://www.google.com/maps?q=${gpsLat},${gpsLng}`,
      });
    },
    () => reject(new Error('Location permission is required for attendance capture')),
    { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
  );
});

const AttendanceModule = ({ user }) => {
  const [todayData, setTodayData] = useState({ record: null, sessions: [], requests: [], sites: [] });
  const [recordsData, setRecordsData] = useState({ records: [], requests: [], sites: [] });
  const [loading, setLoading] = useState(true);
  const [attendanceType, setAttendanceType] = useState('regular');
  const [attendanceNotes, setAttendanceNotes] = useState('');
  const [requestForm, setRequestForm] = useState(emptyRequest);
  const [capturingAction, setCapturingAction] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [todayRes, recordsRes] = await Promise.all([
        axios.get(`${API}/staff/attendance/today`, { withCredentials: true }),
        axios.get(`${API}/staff/attendance/records`, { withCredentials: true }),
      ]);
      setTodayData(todayRes.data);
      setRecordsData(recordsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load attendance');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const activeSession = todayData.record;
  const pendingRequests = useMemo(() => recordsData.requests.filter((item) => item.status === 'pending'), [recordsData.requests]);
  const correctionSessions = useMemo(
    () => recordsData.records.filter((record) => record.date === requestForm.start_date && record.staff_id === user.id),
    [recordsData.records, requestForm.start_date, user.id],
  );

  const captureAttendance = async (endpoint) => {
    setCapturingAction(endpoint);
    try {
      const gpsStamp = await getGpsStamp();
      const payload = endpoint === 'checkin'
        ? { attendance_type: attendanceType, notes: attendanceNotes, ...gpsStamp }
        : { notes: attendanceNotes, ...gpsStamp };
      const { data } = await axios.post(`${API}/staff/attendance/${endpoint}`, payload, { withCredentials: true });
      toast.success(endpoint === 'checkin' ? 'Checked in with GPS' : `Checked out • ${formatDuration(data.duration_minutes)}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Attendance update failed');
    }
    setCapturingAction('');
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/staff/attendance/requests`, requestForm, { withCredentials: true });
      toast.success('Request submitted');
      setRequestForm(emptyRequest);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Request failed');
    }
  };

  const reviewRequest = async (requestId, status) => {
    try {
      await axios.put(`${API}/staff/attendance/requests/${requestId}`, { status, review_notes: '' }, { withCredentials: true });
      toast.success(`Request ${status}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  if (loading) {
    return <div className="flex h-64 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#0A2540]" /></div>;
  }

  return (
    <div className="max-w-full min-w-0 space-y-5" data-testid="attendance-module">
      <div className="rounded-[1.75rem] bg-white p-6 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-sky-600">GPS attendance</p>
        <h2 className="mt-2 text-3xl font-semibold text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }}>Attendance & Duty Requests</h2>
        <p className="mt-2 text-sm text-slate-500">Multiple sessions are allowed each day. Every check-in and check-out captures the device’s exact GPS location.</p>
      </div>

      <div className="grid min-w-0 gap-4 xl:grid-cols-[1.08fr_0.92fr]">
        <div className="min-w-0 space-y-4">
          <div className="rounded-[1.75rem] bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[#0A2540]">Current attendance session</h3>
              <Button onClick={fetchData} variant="outline" size="icon" data-testid="attendance-refresh"><RefreshCw className="h-4 w-4" /></Button>
            </div>

            <div>
              <div>
                <Label htmlFor="attendance-type">Attendance Type</Label>
                <select id="attendance-type" className="mt-2 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm" value={attendanceType} onChange={(e) => setAttendanceType(e.target.value)} data-testid="attendance-type">
                  {ATTENDANCE_TYPES.map((type) => <option key={type} value={type}>{type.replace('_', ' ')}</option>)}
                </select>
              </div>
            </div>

            <div className="mt-4">
              <Label htmlFor="attendance-notes">Notes</Label>
              <textarea id="attendance-notes" className="mt-2 min-h-[96px] w-full rounded-2xl border border-slate-200 px-3 py-3 text-sm" value={attendanceNotes} onChange={(e) => setAttendanceNotes(e.target.value)} placeholder="Optional shift or camp notes" data-testid="attendance-notes" />
            </div>

            <div className="mt-4 rounded-2xl bg-slate-50 p-4" data-testid="attendance-current-card">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#0A2540]"><Clock3 className="h-4 w-4 text-sky-600" /> Open session</div>
              {activeSession ? (
                <div className="mt-3 space-y-2 text-sm text-slate-600">
                  <p>Status: <span className="font-semibold text-[#0A2540]">{activeSession.status}</span></p>
                  <p>Type: {activeSession.attendance_type?.replace('_', ' ')}</p>
                  <p>Checked in: {formatIstTime(activeSession.check_in_at)} IST</p>
                  <p>Check-in GPS: <a href={activeSession.check_in_location?.map_link} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline" data-testid="attendance-current-map-link">Open map</a></p>
                </div>
              ) : <p className="mt-3 text-sm text-slate-500">No open attendance session right now. You can check in again after a completed check-out.</p>}
            </div>

            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              <Button onClick={() => captureAttendance('checkin')} className="rounded-xl bg-[#0A2540]" disabled={Boolean(activeSession) || capturingAction === 'checkin'} data-testid="attendance-checkin">
                <BadgeCheck className="mr-2 h-4 w-4" /> {capturingAction === 'checkin' ? 'Capturing GPS...' : 'Check In with GPS'}
              </Button>
              <Button onClick={() => captureAttendance('checkout')} variant="outline" className="rounded-xl" disabled={!activeSession || capturingAction === 'checkout'} data-testid="attendance-checkout">
                <MapPinned className="mr-2 h-4 w-4" /> {capturingAction === 'checkout' ? 'Capturing GPS...' : 'Check Out with GPS'}
              </Button>
            </div>
          </div>

          <div className="rounded-[1.75rem] bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2"><h3 className="text-lg font-semibold text-[#0A2540]">Today’s sessions</h3><span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700" data-testid="attendance-today-total-duration">Total {formatDuration(todayData.total_duration_minutes || 0)}</span></div>
            <div className="mt-4 space-y-3">
              {todayData.sessions?.length ? todayData.sessions.map((session) => (
                <div key={session.id} className="rounded-2xl border border-slate-200 p-4" data-testid={`attendance-session-${session.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-[#0A2540]">{session.attendance_type?.replace('_', ' ')} attendance</p>
                    <span className={`rounded-full px-3 py-1 text-xs font-medium ${session.status === 'checked_out' ? 'bg-green-100 text-green-700' : 'bg-sky-100 text-sky-700'}`}>{session.status.replace('_', ' ')}</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-600">In: {formatIstTime(session.check_in_at)} IST • <a href={session.check_in_location?.map_link} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline" data-testid={`attendance-session-in-map-${session.id}`}>GPS map</a></p>
                  {session.check_out_location && <p className="mt-1 text-sm text-slate-600">Out: {formatIstTime(session.check_out_at)} IST • <a href={session.check_out_location.map_link} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline" data-testid={`attendance-session-out-map-${session.id}`}>GPS map</a></p>}
                  <p className="mt-2 text-xs font-semibold text-emerald-700" data-testid={`attendance-session-duration-${session.id}`}>Duration: {formatDuration(session.duration_minutes)}</p>
                </div>
              )) : <p className="text-sm text-slate-500">No attendance sessions created today.</p>}
            </div>
          </div>

          <div className="rounded-[1.75rem] bg-white p-5 shadow-sm">
            <h3 className="text-lg font-semibold text-[#0A2540]">Attendance history</h3>
            <div className="mt-4 space-y-3 md:hidden" data-testid="attendance-history-mobile-list">
              {recordsData.records.length === 0 ? <p className="py-5 text-center text-sm text-slate-400">No attendance records yet.</p> : recordsData.records.map((record) => (
                <div key={record.id} className="rounded-xl border border-slate-200 p-3" data-testid={`attendance-history-mobile-${record.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div><p className="text-sm font-semibold text-[#0A2540]">{user.role === 'admin' ? record.staff_name : record.attendance_type?.replace('_', ' ')}</p><p className="mt-1 text-xs text-slate-500">{record.date} · {record.attendance_type?.replace('_', ' ')}</p></div>
                    <a href={record.check_in_location?.map_link} target="_blank" rel="noreferrer" className="text-xs font-semibold text-sky-700 hover:underline" data-testid={`attendance-history-map-mobile-${record.id}`}>GPS map</a>
                  </div>
                  <p className="mt-3 text-sm text-slate-600">{formatIstTime(record.check_in_at)} – {record.check_out_at ? formatIstTime(record.check_out_at) : 'Open'} IST</p>
                  <p className="mt-1 text-xs font-semibold text-emerald-700" data-testid={`attendance-history-duration-mobile-${record.id}`}>Duration: {formatDuration(record.duration_minutes)}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 hidden overflow-x-auto md:block">
              <table className="w-full text-sm">
                <thead className="bg-[#F8FAFC]">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-[#0A2540]">Date</th>
                    {user.role === 'admin' && <th className="px-3 py-2 text-left font-semibold text-[#0A2540]">Staff</th>}
                    <th className="px-3 py-2 text-left font-semibold text-[#0A2540]">Type</th>
                    <th className="px-3 py-2 text-left font-semibold text-[#0A2540]">GPS</th>
                    <th className="px-3 py-2 text-left font-semibold text-[#0A2540]">Time</th>
                    <th className="px-3 py-2 text-left font-semibold text-[#0A2540]">Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {recordsData.records.length === 0 ? <tr><td colSpan={user.role === 'admin' ? 6 : 5} className="px-3 py-6 text-center text-slate-400">No attendance records yet.</td></tr> : recordsData.records.map((record) => (
                    <tr key={record.id} data-testid={`attendance-row-desktop-${record.id}`}>
                      <td className="px-3 py-3 text-slate-600">{record.date}</td>
                      {user.role === 'admin' && <td className="px-3 py-3 font-medium text-[#0A2540]">{record.staff_name}</td>}
                      <td className="px-3 py-3 text-slate-600">{record.attendance_type?.replace('_', ' ')}</td>
                      <td className="px-3 py-3 text-slate-600"><a href={record.check_in_location?.map_link} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline" data-testid={`attendance-history-map-desktop-${record.id}`}>View</a></td>
                      <td className="px-3 py-3 text-slate-600">{formatIstTime(record.check_in_at)} / {formatIstTime(record.check_out_at)} IST</td>
                      <td className="px-3 py-3 font-semibold text-emerald-700" data-testid={`attendance-history-duration-desktop-${record.id}`}>{formatDuration(record.duration_minutes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="min-w-0 space-y-4">
          <div className="rounded-[1.75rem] bg-white p-5 shadow-sm">
            <h3 className="text-lg font-semibold text-[#0A2540]">Apply request</h3>
            <form onSubmit={handleRequestSubmit} className="mt-4 space-y-4" data-testid="attendance-request-form">
              <div><Label>Request Type</Label><select className="mt-2 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm" value={requestForm.request_type} onChange={(e) => setRequestForm((prev) => ({ ...prev, request_type: e.target.value, attendance_record_id: '' }))} data-testid="attendance-request-type">{REQUEST_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}</select></div>
              <div className={`grid gap-4 ${['leave', 'camp_duty'].includes(requestForm.request_type) ? 'sm:grid-cols-2' : ''}`}><div><Label>{requestForm.request_type === 'time_correction' ? 'Attendance Date' : 'Start Date'}</Label><Input type="date" value={requestForm.start_date} onChange={(e) => setRequestForm((prev) => ({ ...prev, start_date: e.target.value, attendance_record_id: '' }))} className="mt-2 h-11 rounded-xl" data-testid="attendance-request-start" required /></div>{['leave', 'camp_duty'].includes(requestForm.request_type) && <div><Label>End Date</Label><Input type="date" value={requestForm.end_date} onChange={(e) => setRequestForm((prev) => ({ ...prev, end_date: e.target.value }))} className="mt-2 h-11 rounded-xl" data-testid="attendance-request-end" required /></div>}</div>
              {requestForm.request_type === 'time_correction' && <div><Label>Attendance Session (IST)</Label><select className="mt-2 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm" value={requestForm.attendance_record_id} onChange={(e) => setRequestForm((prev) => ({ ...prev, attendance_record_id: e.target.value }))} data-testid="attendance-request-session" required><option value="">Select session to correct</option>{correctionSessions.map((session) => <option key={session.id} value={session.id}>{formatIstTime(session.check_in_at)}{session.check_out_at ? ` – ${formatIstTime(session.check_out_at)}` : ' – open'}</option>)}</select>{correctionSessions.length === 0 && <p className="mt-2 text-xs text-amber-700" data-testid="attendance-request-no-session">No attendance session found for this date.</p>}</div>}
              {['time_correction', 'permission'].includes(requestForm.request_type) && <div className="grid gap-4 sm:grid-cols-2"><div><Label>{requestForm.request_type === 'time_correction' ? 'Corrected Check-in' : 'Time From'}</Label><Input type="time" value={requestForm.time_from} onChange={(e) => setRequestForm((prev) => ({ ...prev, time_from: e.target.value }))} className="mt-2 h-11 rounded-xl" data-testid="attendance-request-time-from" required /></div><div><Label>{requestForm.request_type === 'time_correction' ? 'Corrected Check-out' : 'Time To'}</Label><Input type="time" value={requestForm.time_to} onChange={(e) => setRequestForm((prev) => ({ ...prev, time_to: e.target.value }))} className="mt-2 h-11 rounded-xl" data-testid="attendance-request-time-to" required /></div></div>}
              {requestForm.request_type === 'camp_duty' && <div><Label>Camp / Duty Location</Label><Input value={requestForm.location} onChange={(e) => setRequestForm((prev) => ({ ...prev, location: e.target.value }))} className="mt-2 h-11 rounded-xl" data-testid="attendance-request-location" required /></div>}
              <div><Label>Reason</Label><textarea className="mt-2 min-h-[96px] w-full rounded-2xl border border-slate-200 px-3 py-3 text-sm" value={requestForm.reason} onChange={(e) => setRequestForm((prev) => ({ ...prev, reason: e.target.value }))} placeholder="Add the reason for this request" data-testid="attendance-request-reason" required /></div>
              <Button type="submit" className="rounded-xl bg-[#0A2540]" data-testid="attendance-request-submit"><Send className="mr-2 h-4 w-4" /> Submit Request</Button>
            </form>
          </div>

          <div className="rounded-[1.75rem] bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between"><h3 className="text-lg font-semibold text-[#0A2540]">Requests</h3><span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">{recordsData.requests.length} total</span></div>
            <div className="mt-4 space-y-3">
              {recordsData.requests.length === 0 ? <p className="text-sm text-slate-500">No leave or duty requests yet.</p> : recordsData.requests.map((item) => (
                <div key={item.id} className="rounded-2xl border border-slate-200 p-4" data-testid={`attendance-request-card-${item.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2"><div><p className="text-sm font-semibold text-[#0A2540]">{requestLabel(item.request_type)}</p><p className="mt-1 text-xs text-slate-500">{item.start_date} {item.end_date && item.end_date !== item.start_date ? `to ${item.end_date}` : ''}</p></div><span className={`rounded-full px-3 py-1 text-xs font-medium ${item.status === 'approved' ? 'bg-green-100 text-green-700' : item.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`} data-testid={`attendance-request-status-${item.id}`}>{item.status}</span></div>
                  {item.request_type === 'time_correction' && <p className="mt-2 text-xs font-medium text-sky-700">Requested time: {item.time_from} – {item.time_to} IST</p>}
                  <p className="mt-3 text-sm text-slate-600">{item.reason}</p>
                  {user.role === 'admin' && item.status === 'pending' && <div className="mt-4 flex gap-2"><Button onClick={() => reviewRequest(item.id, 'approved')} className="rounded-xl bg-green-600 hover:bg-green-700" data-testid={`attendance-approve-${item.id}`}>Approve</Button><Button onClick={() => reviewRequest(item.id, 'rejected')} variant="outline" className="rounded-xl border-red-200 text-red-600 hover:bg-red-50" data-testid={`attendance-reject-${item.id}`}>Reject</Button></div>}
                </div>
              ))}
            </div>
            {user.role === 'admin' && pendingRequests.length > 0 && <p className="mt-3 text-xs text-slate-500">Pending requests need Staff Admin approval.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceModule;